/* DryRunStrip — "Test on {project}" with full output.
 *
 * Spec 07. Designer-grade output: counts + actual values from the
 * matched nodes (id, label, every field the predicate touched). The
 * Promote CTA only unlocks after a dry-run has reported.
 */

import { useCallback, useMemo, useState } from "react";
import { Play, Loader2, AlertCircle, CheckCircle2, Cpu, Tag } from "lucide-react";
import type {
  Predicate,
  ExtendedTerm,
  NodePattern,
} from "../../../types/rule";
import type { GraphNode } from "../../../types/ontology";
import { dryRun, type DryRunResult } from "../../../lib/ruleEvaluate";
import { cls } from "../../../lib/cls";

interface DryRunStripProps {
  predicate: Predicate;
  projectName: string;
  graphNodes: ReadonlyArray<GraphNode>;
  disabled?: boolean;
  onResult?: (r: DryRunResult) => void;
}

/* Walk the AST and collect every field path it touches so we can
 * print real values from the matched nodes ("SKU.leadDays = 92 days"). */
function collectFieldPaths(p: Predicate, out: Set<string>): void {
  const handleTerm = (t: ExtendedTerm | NodePattern): void => {
    if ("kind" in t && t.kind === "field") out.add(t.path);
    if ("kind" in t && t.kind === "agg") {
      handleTerm(t.over);
      if (t.field) out.add(t.field.path);
      if (t.via) handleTerm(t.via.target);
    }
  };
  switch (p.kind) {
    case "cmp":          handleTerm(p.lhs); handleTerm(p.rhs); return;
    case "in_range":
    case "not_in_range": handleTerm(p.value); return;
    case "in_set":
    case "not_in_set":   handleTerm(p.value); return;
    case "graph_pattern":
    case "exists":
    case "forbidden": {
      const subj = p.kind === "graph_pattern" ? p.subject : p.pattern.subject;
      const obj  = p.kind === "graph_pattern" ? p.object   : p.pattern.object;
      if (subj.filterAttr) out.add(`${subj.nodeKind}.${subj.filterAttr}`);
      if (obj.kind === "pattern" && obj.filterAttr) {
        out.add(`${obj.nodeKind}.${obj.filterAttr}`);
      }
      return;
    }
    case "and":
    case "or":
      collectFieldPaths(p.left, out);
      collectFieldPaths(p.right, out);
      return;
    case "not":
      collectFieldPaths(p.inner, out);
      return;
  }
}

function fieldValueOf(node: GraphNode, path: string): string | undefined {
  const [kind, field] = path.split(".");
  if (!kind || !field) return undefined;
  if (node.kind !== kind) return undefined;
  const v = node.attrs[field];
  if (v === undefined) return undefined;
  if (typeof v === "number") return String(v);
  if (typeof v === "boolean") return String(v);
  return String(v);
}

interface HitRow {
  id: string;
  label: string;
  kind: string;
  values: Array<{ path: string; value: string }>;
}

export function DryRunStrip({ predicate, projectName, graphNodes, disabled, onResult }: DryRunStripProps): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<DryRunResult | null>(null);

  const fieldPaths = useMemo(() => {
    const s = new Set<string>();
    collectFieldPaths(predicate, s);
    return Array.from(s);
  }, [predicate]);

  const hits = useMemo<HitRow[]>(() => {
    if (!result) return [];
    return result.matched.map((id) => {
      const n = graphNodes.find((node) => node.id === id);
      if (!n) return { id, label: id, kind: "?", values: [] };
      const values = fieldPaths
        .map((p) => ({ path: p, value: fieldValueOf(n, p) }))
        .filter((v): v is { path: string; value: string } => v.value !== undefined);
      return { id: n.id, label: n.label, kind: n.kind, values };
    });
  }, [result, graphNodes, fieldPaths]);

  const run = useCallback(() => {
    setBusy(true);
    requestAnimationFrame(() => {
      const r = dryRun(predicate, graphNodes);
      setResult(r);
      setBusy(false);
      onResult?.(r);
    });
  }, [predicate, graphNodes, onResult]);

  return (
    <div className="panel p-4 space-y-3">
      <div className="flex items-center gap-3 flex-wrap">
        <button
          type="button"
          onClick={run}
          disabled={disabled || busy}
          className={cls(
            "btn btn-outline !py-2 !px-3 !text-[13px]",
            (disabled || busy) ? "opacity-50 cursor-not-allowed" : "",
          )}
          title={disabled ? "Fix the validator issues first" : `Run the rule against ${projectName} locally.`}
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
          <span>Test on {projectName}</span>
        </button>

        {result ? (
          <div className="flex items-center gap-2 flex-wrap">
            <span className={cls(
              "pill border tabular",
              result.matched.length > 0 ? "bg-fern/10 border-fern/30 text-fern" : "bg-cream-dark border-border text-ink-700",
            )}>
              <CheckCircle2 className="w-3 h-3 inline mr-1" />
              {result.matched.length} hit{result.matched.length === 1 ? "" : "s"} on this project
            </span>
            {result.unevaluable.length > 0 ? (
              <span className="pill border bg-cream-dark border-border text-ink-700 tabular" title={result.unevaluable.join(", ")}>
                <Cpu className="w-3 h-3 inline mr-1" />
                {result.unevaluable.length} predicate{result.unevaluable.length === 1 ? "" : "s"} evaluate server-side
              </span>
            ) : (
              <span className="pill border bg-fern/10 border-fern/30 text-fern tabular">fully evaluated in-browser</span>
            )}
          </div>
        ) : (
          <span className="text-[12px] text-ink-500 inline-flex items-center gap-1.5">
            <AlertCircle className="w-3 h-3" />
            Run the test to see how this rule behaves on the real graph before promote.
          </span>
        )}
      </div>

      {/* Actual matched values — designers see exactly what fired and why. */}
      {result && result.matched.length > 0 ? (
        <div className="border-t border-border pt-3">
          <div className="eyebrow mb-2">Matched nodes — actual values</div>
          <ul className="space-y-2">
            {hits.slice(0, 10).map((h) => (
              <li key={h.id} className="panel-subtle p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Tag className="w-3 h-3 text-ink-500" />
                  <span className="text-[12px] font-semibold text-ink-950">{h.label}</span>
                  <span className="text-[10px] font-mono text-ink-500">{h.kind} · {h.id}</span>
                </div>
                {h.values.length === 0 ? (
                  <div className="text-[11px] text-ink-500 italic">Predicate touches this node by type — values evaluated server-side.</div>
                ) : (
                  <ul className="grid grid-cols-2 gap-x-3 gap-y-1">
                    {h.values.map((v) => (
                      <li key={v.path} className="text-[11px] flex items-baseline gap-1">
                        <span className="font-mono text-ink-500">{v.path}</span>
                        <span className="font-mono text-ink-950">= {v.value}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
            {hits.length > 10 ? (
              <li className="text-[10px] text-ink-500 italic">+ {hits.length - 10} more matches</li>
            ) : null}
          </ul>
        </div>
      ) : null}

      {result && result.unevaluable.length > 0 ? (
        <div className="border-t border-border pt-3 text-[11px] text-ink-500 leading-snug">
          <span className="font-mono">{result.unevaluable.join(", ")}</span> predicate kinds run on the agent's
          typed SPARQL evaluator (graph traversal, aggregation across nodes). The browser preview shows scalar
          matches only; the agent's count may differ.
        </div>
      ) : null}
    </div>
  );
}
